/**
 * 数据流动画组件
 * 用于 Hero 区域背景，体现"意识数字化"的科技感
 * 
 * 设计思路：
 * - 纯 CSS 实现，无需 JS 动画
 * - 流动的粒子/光点效果
 * - 深色背景上的紫色/蓝色渐变
 */

import { cn } from '@/shared/lib/utils'

interface DataFlowAnimationProps {
  className?: string
  intensity?: 'low' | 'medium' | 'high'
}

export function DataFlowAnimation({
  className,
  intensity = 'medium',
}: DataFlowAnimationProps) {
  const particleCount = intensity === 'low' ? 20 : intensity === 'medium' ? 40 : 60

  return (
    <div
      className={cn(
        'absolute inset-0 overflow-hidden',
        className
      )}
    >
      {/* 基础渐变背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900" />
      
      {/* 网格图案 */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      
      {/* 流动粒子 */}
      <div className="absolute inset-0">
        {Array.from({ length: particleCount }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-float"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 3 === 0 
                ? 'rgba(139, 92, 246, 0.6)' 
                : i % 3 === 1
                ? 'rgba(168, 85, 247, 0.5)'
                : 'rgba(99, 102, 241, 0.4)',
              boxShadow: `0 0 ${Math.random() * 10 + 5}px currentColor`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 10}s`,
            }}
          />
        ))}
      </div>
      
      {/* 渐变光晕 */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl animate-pulse-slow delay-1000" />
      
      {/* 扫描线效果 */}
      <div 
        className="absolute inset-0 opacity-5 animate-scan"
        style={{
          background: 'linear-gradient(0deg, transparent 0%, rgba(139, 92, 246, 0.5) 50%, transparent 100%)',
          backgroundSize: '100% 200%',
        }}
      />
    </div>
  )
}

/**
 * 添加到全局 CSS 的动画定义
 * 需要在 tailwind.config.js 或 globals.css 中添加：
 * 
 * @keyframes float {
 *   0%, 100% { transform: translateY(0) translateX(0); opacity: 0.6; }
 *   50% { transform: translateY(-20px) translateX(10px); opacity: 1; }
 * }
 * 
 * @keyframes pulse-slow {
 *   0%, 100% { opacity: 0.3; transform: scale(1); }
 *   50% { opacity: 0.5; transform: scale(1.1); }
 * }
 * 
 * @keyframes scan {
 *   0% { background-position: 0% 200%; }
 *   100% { background-position: 0% -200%; }
 * }
 * 
 * .animate-float { animation: float 8s ease-in-out infinite; }
 * .animate-pulse-slow { animation: pulse-slow 6s ease-in-out infinite; }
 * .animate-scan { animation: scan 8s linear infinite; }
 */

// 简化版：使用内联样式的动画
export function DataFlowAnimationSimple({ className }: { className?: string }) {
  return (
    <div className={cn('absolute inset-0 overflow-hidden', className)}>
      {/* 深色渐变背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-violet-950/90 to-slate-900" />
      
      {/* 微弱网格 */}
      <div 
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(139, 92, 246, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 92, 246, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
      
      {/* 光晕 - 使用 CSS animation */}
      <div 
        className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[100px]"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
          animation: 'pulse 8s ease-in-out infinite',
        }}
      />
      <div 
        className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full blur-[80px]"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, transparent 70%)',
          animation: 'pulse 6s ease-in-out infinite 2s',
        }}
      />
    </div>
  )
}

// 导出默认使用简化版
export default DataFlowAnimationSimple
